import api from "./api";
import type {
  AttributeLookupOnloadResponse,
  AllergenDeclarationListResponse,
  CreateOrUpdateAllergenRequest,
  CreateOrUpdateAllergenResponse,
  AllergenDetailResponse,
  SaveProductSpecificationResponse,
  SaveSiteAllergensRequest,
  CreateSiteAllergensResponse,
  AllergensSensitivitiesByAssessmentResponse
} from "@/components/ActiveProjects/AllergensAndSensitivities/types";

const { pimcoreServiceAPI, aggregatedServiceAPI } = api;
const ATTRIBUTE_LOOKUP_PATH = "/attributeLookup/getAttributeLookupListing/statementToBestDescribe,labelDeclaration";
const SAVE_FOR_SPECIFIC_ALLERGEN_DETAIL_PATH = "/ProductVendorAggregator/allergenDeclaration/save";
const GET_ALLERGEN_DECLARATION_BY_PRODUCT_SPEC_PATH = "/ProductVendorAggregator/allergenDeclaration/productSpecification";
const GET_ALLERGEN_DETAILS_PATH = "/ProductVendorAggregator/allergenDeclaration/";
const GET_ALLERGENS_SENSITIVITIES_BY_ASSESSMENT_PATH = "/ProductVendorAggregator/section/allergensSensitivities";
const SAVE_SITE_ALLERGENS_PATH = "/ProductVendorAggregator/saveSiteAllergens";

const AllergensAndSensitivitiesService = {
  // Prevent indefinite hangs (which can leave global loaders stuck). Axios timeout is in ms.
  _timeoutMs: 120_000,
  /**
   * Get attribute lookup data for allergen form dropdowns
   * @param additionalParams - Optional additional parameters to include in the query
   * @returns Attribute lookup onload data including gluten, treeNut, fish, milk, labelDeclaration, statementToBestDescribe
   */
  getAttributeOnloadData: async (additionalParams?: string[]): Promise<AttributeLookupOnloadResponse> => {
    try {
      let path = ATTRIBUTE_LOOKUP_PATH;
      
      // Add additional parameters if provided
      if (additionalParams && additionalParams.length > 0) {
        path += `,${additionalParams.join(',')}`;
      }
      
      const resp = await pimcoreServiceAPI.get<AttributeLookupOnloadResponse>(path, { timeout: AllergensAndSensitivitiesService._timeoutMs });
      return resp.data;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Save or update a specific allergen detail
   * @param payload - Allergen detail to save or update
   * @returns Saved allergen detail with ID
   */
  saveForSpecificAllergenDetail: async (payload: CreateOrUpdateAllergenRequest): Promise<CreateOrUpdateAllergenResponse> => {
    try {
      const resp = await aggregatedServiceAPI.post<CreateOrUpdateAllergenResponse>(
        SAVE_FOR_SPECIFIC_ALLERGEN_DETAIL_PATH,
        payload,
        { timeout: AllergensAndSensitivitiesService._timeoutMs }
      );
      return resp.data;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Get all allergen declarations for a specific product specification and vendor assessment
   * @param productSpecificationId - Product specification ID
   * @param vendorAssessmentId - Vendor assessment ID
   * @returns List of allergen declarations with contains/may contain statements
   */
  getAllergenDeclarationByProductSpec: async (
    productSpecificationId: string | number, 
    vendorAssessmentId: string | number
  ): Promise<AllergenDeclarationListResponse> => {
    try {
      const path = `${GET_ALLERGEN_DECLARATION_BY_PRODUCT_SPEC_PATH}/${productSpecificationId}?vendorAssessmentId=${vendorAssessmentId}`;
      const resp = await aggregatedServiceAPI.get<AllergenDeclarationListResponse>(path, { timeout: AllergensAndSensitivitiesService._timeoutMs });
      return resp.data;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Get a specific allergen declaration by ID with full details including attachments
   * @param id - Allergen declaration ID
   * @returns Full allergen declaration details including attachments
   */
  getAllergenDeclarationById: async (id: string | number): Promise<AllergenDetailResponse> => {
    try {
      const path = `${GET_ALLERGEN_DETAILS_PATH}${id}`;
      const resp = await aggregatedServiceAPI.get<AllergenDetailResponse>(path, { timeout: AllergensAndSensitivitiesService._timeoutMs });
      return resp.data;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Save site allergens data
   * POST /ProductVendorAggregator/saveSiteAllergens?vendorAssessmentId={vendorAssessmentId}
   * @param payload - Site allergens data to save
   * @param vendorAssessmentId - Vendor assessment ID (required query parameter)
   * @returns Saved site allergens response
   */
  saveSiteAllergens: async (payload: SaveSiteAllergensRequest, vendorAssessmentId: string | number): Promise<SaveProductSpecificationResponse> => {
    if (!vendorAssessmentId) {
      throw new Error('Cannot save site allergens: vendorAssessmentId is missing');
    }
    try {
      const path = `${SAVE_SITE_ALLERGENS_PATH}?vendorAssessmentId=${vendorAssessmentId}`;
      const resp = await aggregatedServiceAPI.post<SaveProductSpecificationResponse>(
        path,
        payload,
        { timeout: AllergensAndSensitivitiesService._timeoutMs }
      );
      return resp.data;
    } catch (error) {
      console.error("❌ [AllergensAndSensitivitiesService] Error saving site allergens:", error);
      throw error;
    }
  },

  /**
   * Create a new site allergens section
   * POST /shared/siteAllergens/createSiteAllergens
   * @param payload - Site allergens creation data with section name and vendor assessment ID
   * @returns Promise with created site allergens details
   */
  createSiteAllergens: async (payload: {
    sectionName: string;
    vendorAssessment: { id: number };
  }): Promise<CreateSiteAllergensResponse> => {
    try {
      const url = 'shared/siteAllergens/createSiteAllergens';
      const resp = await pimcoreServiceAPI.post<CreateSiteAllergensResponse>(url, payload, { timeout: AllergensAndSensitivitiesService._timeoutMs });
      return resp.data;
    } catch (error) {
      console.error("❌ [AllergensAndSensitivitiesService] Error creating site allergens:", error);
      throw error;
    }
  },

  /**
   * Delete a site allergens section
   * POST /shared/siteAllergens/deleteSiteAllergens
   * @param id - Site allergens section ID to delete
   * @returns Promise with deletion response
   */
  deleteSiteAllergens: async (id: string | number): Promise<any> => {
    if (id == null || String(id).trim() === '') {
      throw new Error('Cannot delete site allergens section: id is missing');
    }

    const idNum = Number(String(id).trim());
    if (!Number.isFinite(idNum)) {
      throw new Error('Cannot delete site allergens section: id is not a valid number');
    }

    try {
      const url = 'shared/siteAllergens/deleteSiteAllergens';
      const resp = await pimcoreServiceAPI.post(url, { id: idNum }, { timeout: AllergensAndSensitivitiesService._timeoutMs });
      return resp.data;
    } catch (error) {
      console.error("❌ [AllergensAndSensitivitiesService] Error deleting site allergens:", error);
      throw error;
    }
  },

  /**
   * Get allergens and sensitivities data by assessment ID
   * GET /ProductVendorAggregator/section/allergensSensitivities?assessmentId={assessmentId}
   * @param assessmentId - Vendor assessment ID
   * @returns Promise with site allergens data including sections, allergens declarations, other declarations, and transitionObject
   */
  getAllergensSensitivitiesByAssessmentId: async (assessmentId: string | number): Promise<AllergensSensitivitiesByAssessmentResponse> => {
    if (!assessmentId) {
      throw new Error('Cannot fetch allergens sensitivities: assessmentId is missing');
    }

    try {
      const path = `${GET_ALLERGENS_SENSITIVITIES_BY_ASSESSMENT_PATH}?assessmentId=${assessmentId}`;
      const resp = await aggregatedServiceAPI.get<AllergensSensitivitiesByAssessmentResponse>(path, { timeout: AllergensAndSensitivitiesService._timeoutMs });
      return resp.data;
    } catch (error) {
      console.error("❌ [AllergensAndSensitivitiesService] Error fetching allergens sensitivities:", error);
      throw error;
    }
  },
};

export default AllergensAndSensitivitiesService;
